import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Observable, Subscription} from 'rxjs';
import {AllergyAddCardComponent} from './allergy-add-card/allergy-add-card.component';
import {AllergiesRepositoryService} from '../../allergies-repository/allergies-repository.service';
import {Allergy} from '../../allergies-repository/entities/allergy';
import {UserSettings} from '../../user-repository/entities/user-settings';
import {AppMatAutocompleteComponent} from '../../../shared/components/app-mat-autocomplete/app-mat-autocomplete.component';
import {PatientService} from '../../patients/services/patient.service';
import {CoreService} from '../../../core/services/core.service';
import {WebsocketService} from '../../../core/services/websocket.service';
import {PatientEvent, PatientEventsService} from '../../patients/services/patient-events.service';
import {Patient} from '../../patient-repository/entities/patient';
import {filter, map} from 'rxjs/operators';

@Component({
    selector: 'app-allergies-widget',
    templateUrl: './allergies-widget.component.html',
    styleUrl: './allergies-widget.component.scss',
    standalone: false
})
export class AllergiesWidgetComponent implements OnInit, OnDestroy {

  error = '';
  loading = false;
  settings: UserSettings;
  allergies: Allergy[] = [];
  @Input() readonly = false;
  @ViewChild(AppMatAutocompleteComponent) autocomplete: AppMatAutocompleteComponent;
  private subscriptions: Subscription[] = [];
  private channelName = '';
  private readonly eventName = 'allergy_list_changed';

  constructor(
    private matDialog: MatDialog,
    private allergyRepository: AllergiesRepositoryService,
    private patientService: PatientService,
    private coreService: CoreService,
    private pushService: WebsocketService,
    private patientEventService: PatientEventsService
  ) {
  }

  get hasError(): boolean {
    return this.error.length > 0;
  }

  get hasAllergies(): boolean {
    return this.allergies.length > 0;
  }

  get patientId(): number {
    return this.patientService.currentPatient.id;
  }

  get patient(): Patient {
    return this.patientService.currentPatient;
  }

  searchAllergies$ = (query: string): Observable<Allergy[]> => {
    return this.allergyRepository.query(query)
      .pipe(map(response => response));
  };

  ngOnInit(): void {
    this.channelName = this.patientService.currentPatientEventsChannelName;
    this.settings = this.coreService.settings;
    this.subscriptions.push(this.patientEventService.currentPatient$.subscribe(() => this.loadPatientAllergies()));
    this.subscriptions.push(this.patientService.patientAllergies$.subscribe(allergies => this.allergies = [...allergies]));
    this.subscriptions.push(this.patientEventService.events$.pipe(filter(e => e.type === 'allergy_item_deleted')).subscribe(() => this.loadPatientAllergies()));
    this.pushService.subscribe(this.channelName, this.eventName, data => {
      console.log('AllergiesWidgetComponent: pushMessageReceived', data);
      this.loadPatientAllergies();
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.pushService.unsubscribe(this.channelName, this.eventName);
  }

  onAddNewItem() {
    const dialogRef = this.matDialog.open(AllergyAddCardComponent, {
      panelClass: 'mat-dialog-responsive'
    });
    this.subscriptions.push(dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.autocomplete.search(result.allergy);
      }
    }));
  }

  onItemSelected(allergy: Allergy) {
    this.allergies.push(allergy);
    this.addAllergyToPatient(allergy);
    this.autocomplete.cancelSearch();
  }

  private loadPatientAllergies() {
    if (!this.patientService.hasPatientDetails) {
      return;
    }
    this.patientService.loadPatientAllergies();
  }

  private addAllergyToPatient(allergy: Allergy) {
    this.error = '';
    this.subscriptions.push(this.allergyRepository.addAllergyToPatient(this.patientId, allergy).subscribe(() => {
      this.patientEventService.emitEvent(new PatientEvent('allergy_item_added'));
    }, error => {
      this.error = 'Could not add new allergy to this patient';
      this.loadPatientAllergies();
    }));
  }

}
